<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { PROJECT_CONSTANT } from "../../services/projectService.js";
import { useProjectsByTab } from "../../composables/useProjectsByTab.js";
import { useInfiniteScroll } from "../../composables/useInfiniteScroll.js";
import { truncateMiddle } from "../../composables/useTextTruncate.js";
import Spinner from "../../components/ui/Spinner.vue";
import ProjectCardMenu from "../../components/ui/ProjectCardMenu.vue";
import projectCardImage from "../../../assets/images/projectCardImage.svg";

const router = useRouter();

const TABS = [
  { key: PROJECT_CONSTANT.PROJECT_TYPES.ALL,            label: "All Projects" },
  { key: PROJECT_CONSTANT.PROJECT_TYPES.PERSONAL,        label: "Personal" },
  { key: PROJECT_CONSTANT.PROJECT_TYPES.SHARED_WITH_ME,  label: "Shared with me" },
];
const activeTab = ref(PROJECT_CONSTANT.PROJECT_TYPES.ALL);

// Shared with the create-project modal so a newly created project can be
// picked up silently, without this view needing to know who created it.
const { state: tabState, loadFirstPage, loadMore, resort } = useProjectsByTab();

const current = computed(() => tabState[activeTab.value]);

onMounted(() => loadFirstPage(activeTab.value));
watch(activeTab, (tabKey) => loadFirstPage(tabKey));

const SORT_OPTIONS = [
  { by: "LAST_MODIFIED", label: "Last modified" },
  { by: "NAME",           label: "Name" },
  { by: "DATE_ADDED",     label: "Date created" },
];
const sortBy = ref("LAST_MODIFIED");
const sortDir = ref("DESC"); // ASC | DESC
const showSortMenu = ref(false);
const viewMode = ref("grid"); // grid | list

const sortLabel = computed(() => SORT_OPTIONS.find((o) => o.by === sortBy.value)?.label ?? "");
// Sort is applied server-side (see useProjectsByTab.js's resort), so the
// list already comes back in the right order — no client-side re-sort here.
const sortedProjects = computed(() => current.value.projects);

// Sort is applied server-side, so changing it re-fetches from the first page
watch([sortBy, sortDir], () => resort(activeTab.value, sortBy.value, sortDir.value));

function setSort(by) {
  sortBy.value = by;
  showSortMenu.value = false;
}
function toggleSortDir() {
  sortDir.value = sortDir.value === "ASC" ? "DESC" : "ASC";
}

function openProject(id) {
  router.push({ name: "v2-project", params: { projectId: id } });
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

const ROLE_LABELS = { OWNER: "Owner", EDITOR: "Editor", CONTRIBUTOR: "Contributor", VIEWER: "Viewer" };

function initials(person) {
  const label = person?.name || person?.email || "?";
  return label.slice(0, 2).toUpperCase();
}

function personTooltip(person) {
  if (person?.name && person?.email) return `${person.name} (${person.email})`;
  return person?.name || person?.email || "";
}

function ownerPerson(project) {
  return project.ownerProfile ?? null;
}

function otherCollaborators(project) {
  return (project.topCollaborators ?? [])
    .filter((c) => c.collaboratorRole !== "OWNER")
    .map((c) => ({ ...c.user, role: c.collaboratorRole }))
    .slice(0, 3);
}

function otherOverflowCount(project) {
  const ownerCount = project.ownerProfile ? 1 : 0;
  return Math.max(0, (project.totalCollaboratorCount ?? 0) - ownerCount - otherCollaborators(project).length);
}

const sentinelEl = ref(null);
useInfiniteScroll(sentinelEl, () => loadMore(activeTab.value));
</script>

<template>
  <div class="flex flex-col px-6 bg-gray-50 dark:bg-slate-900 min-h-full" @click="showSortMenu = false">

    <!-- Tabs -->
    <div class="flex gap-1 mb-5 pt-2 -ml-6 -mr-6 px-8 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
      <button
        v-for="tab in TABS"
        :key="tab.key"
        :class="activeTab === tab.key
          ? 'text-brand border-brand font-semibold'
          : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-300'"
        class="flex items-center gap-1.5 px-3 py-2.5 text-sm border-0 border-b-2 bg-transparent cursor-pointer transition-colors -mb-px"
        @click="activeTab = tab.key"
      >
        <svg v-if="tab.key === 'ALL'" width="15" height="15" viewBox="0 0 20 20" fill="none">
          <path d="M11.2507 6.25065L10.4744 4.82756C10.1824 4.29213 9.62117 3.95898 9.01126 3.95898H5.62565C4.70518 3.95898 3.95898 4.70518 3.95898 5.62565V9.16732M16.0423 8.12565V14.3757C16.0423 15.2961 15.2961 16.0423 14.3757 16.0423H5.62565C4.70518 16.0423 3.95898 15.2961 3.95898 14.3757V6.45898H14.3757C15.2961 6.45898 16.0423 7.20518 16.0423 8.12565Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <svg v-else-if="tab.key === 'PERSONAL'" width="15" height="15" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="3.5" stroke="currentColor" stroke-width="1.8" />
          <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none">
          <circle cx="9" cy="8" r="3" stroke="currentColor" stroke-width="1.8" />
          <path d="M2.5 19c0-3 2.9-5.5 6.5-5.5s6.5 2.5 6.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          <path d="M15.5 5.5a3 3 0 0 1 0 5.8M18.5 19c0-2.5-1.6-4.6-4-5.3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        {{ tab.label }}
      </button>
    </div>

    <!-- Sort + view toggle -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <div class="relative" @click.stop>
          <button
            class="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-500 dark:text-slate-400 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            @click="showSortMenu = !showSortMenu"
          >
            Sort by <span class="font-semibold text-slate-800 dark:text-slate-100">{{ sortLabel }}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div
            v-if="showSortMenu"
            class="absolute top-full left-0 mt-2 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-20 overflow-hidden"
          >
            <button
              v-for="opt in SORT_OPTIONS"
              :key="opt.by"
              :class="sortBy === opt.by ? 'text-brand bg-orange-50 dark:bg-orange-950/30' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'"
              class="flex items-center w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors"
              @click="setSort(opt.by)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <button
          class="flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          :title="sortDir === 'ASC' ? 'Ascending' : 'Descending'"
          @click="toggleSortDir"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M8 5v14M8 5L4 9M8 5l4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M16 19V5M16 19l4-4M16 19l-4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <div class="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
        <button
          :class="viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100' : 'bg-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'"
          class="flex items-center justify-center w-8 h-8 rounded-lg border-0 cursor-pointer transition-colors"
          @click="viewMode = 'grid'"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2" />
            <rect x="13" y="3" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2" />
            <rect x="3" y="13" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2" />
            <rect x="13" y="13" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="2" />
          </svg>
        </button>
        <button
          :class="viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100' : 'bg-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'"
          class="flex items-center justify-center w-8 h-8 rounded-lg border-0 cursor-pointer transition-colors"
          @click="viewMode = 'list'"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="4" height="4" rx="1" fill="currentColor" />
            <path d="M10 6h11" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <rect x="3" y="10" width="4" height="4" rx="1" fill="currentColor" />
            <path d="M10 12h11" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <rect x="3" y="16" width="4" height="4" rx="1" fill="currentColor" />
            <path d="M10 18h11" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Loading (first page) -->
    <div v-if="current.isLoading" class="flex justify-center py-20">
      <Spinner size="48px" />
    </div>

    <!-- Empty state -->
    <div v-else-if="sortedProjects.length === 0" class="flex flex-col items-center justify-center min-h-[50vh] gap-3 text-slate-400 dark:text-slate-500">
      <div class="flex items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 mb-1" style="width:72px;height:72px">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
          <path d="M11.25 6.25L10.47 4.82C10.18 4.29 9.62 3.96 9.01 3.96H5.62C4.7 3.96 3.96 4.7 3.96 5.62V9.17M16.04 8.12V14.37C16.04 15.3 15.3 16.04 14.37 16.04H5.62C4.7 16.04 3.96 15.3 3.96 14.37V6.46H14.37C15.3 6.46 16.04 7.2 16.04 8.12Z"
            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100 m-0">No projects yet</h2>
      <p class="text-sm m-0">Projects will appear here</p>
    </div>

    <template v-else>
      <!-- Grid view -->
      <div v-if="viewMode === 'grid'" class="grid grid-cols-[repeat(auto-fill,minmax(275px,1fr))] gap-4">
        <div
          v-for="p in sortedProjects"
          :key="p.id"
          class="relative bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden cursor-pointer hover:shadow-md dark:hover:border-slate-600 transition-all"
          @click="openProject(p.id)"
        >
          <div class="relative h-44 bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
            <img :src="projectCardImage" alt="" class="w-full h-full object-cover" />
            <span class="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 dark:bg-slate-800/90 text-[11px] font-medium text-slate-600 dark:text-slate-300 shadow-sm">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="8" r="3" stroke="currentColor" stroke-width="1.8" />
                <path d="M2.5 19c0-3 2.9-5.5 6.5-5.5s6.5 2.5 6.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              </svg>
              {{ ROLE_LABELS[p.userRole] ?? p.userRole }}
            </span>
          </div>
          <div class="p-4">
            <div class="flex items-center justify-between gap-2">
              <p :title="p.name" class="text-base font-semibold text-slate-800 dark:text-slate-100 truncate m-0">{{ truncateMiddle(p.name) }}</p>
              <ProjectCardMenu :project="p" />
            </div>
            <p class="text-xs text-slate-400 dark:text-slate-500 m-0 mt-1">
              {{ p.totalDocuments === 1 ? "1 document" : `${p.totalDocuments ?? 0} documents` }}
            </p>
            <p class="text-xs text-slate-400 dark:text-slate-500 m-0 mt-0.5">Last modified {{ formatDate(p.updatedAt) }}</p>
            <div class="flex items-center gap-2 mt-2.5">
              <div
                v-if="ownerPerson(p)"
                :title="personTooltip(ownerPerson(p))"
                class="flex items-center justify-center w-6 h-6 rounded-full bg-[#7ba7d4] text-white text-[10px] font-semibold border-2 border-white dark:border-slate-800 shrink-0"
              >
                {{ initials(ownerPerson(p)) }}
              </div>
              <span v-if="ownerPerson(p) && otherCollaborators(p).length" class="text-slate-300 dark:text-slate-600 shrink-0">&bull;</span>
              <div class="flex items-center">
                <div
                  v-for="(person, index) in otherCollaborators(p)"
                  :key="person.email ?? index"
                  :title="personTooltip(person)"
                  :style="{ marginLeft: index !== 0 ? '-8px' : '0' }"
                  class="flex items-center justify-center w-6 h-6 rounded-full bg-[#7ba7d4] text-white text-[10px] font-semibold border-2 border-white dark:border-slate-800"
                >
                  {{ initials(person) }}
                </div>
                <span v-if="otherOverflowCount(p) > 0" class="ml-1 text-[11px] text-slate-400 dark:text-slate-500">+{{ otherOverflowCount(p) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- List view -->
      <table v-else class="w-full border-collapse bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
        <thead>
          <tr class="bg-slate-100 dark:bg-slate-700">
            <th class="w-[38%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Project name</th>
            <th class="w-[15%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Documents</th>
            <th class="w-[15%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Role</th>
            <th class="w-[27%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Last modified</th>
            <th class="w-[5%] border-b border-slate-200 dark:border-slate-700" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="p in sortedProjects"
            :key="p.id"
            class="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
            @click="openProject(p.id)"
          >
            <td class="px-4 py-3.5 text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{{ p.name }}</td>
            <td class="px-4 py-3.5 text-sm text-slate-500 dark:text-slate-400">{{ p.totalDocuments ?? 0 }}</td>
            <td class="px-4 py-3.5 text-sm text-slate-500 dark:text-slate-400">{{ ROLE_LABELS[p.userRole] ?? p.userRole }}</td>
            <td class="px-4 py-3.5 text-sm text-slate-500 dark:text-slate-400">{{ formatDate(p.updatedAt) }}</td>
            <td class="px-2 py-3.5 text-right" @click.stop>
              <ProjectCardMenu :project="p" />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Infinite scroll sentinel -->
      <div ref="sentinelEl" class="h-px" />
      <div v-if="current.isLoadingMore" class="flex justify-center py-6"><Spinner size="24px" /></div>
      <p v-else-if="!current.hasMore" class="text-center text-xs text-slate-400 dark:text-slate-500 py-6 m-0">You've reached the end</p>
    </template>
  </div>
</template>
